import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UrlSegment } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError, first, switchMap, tap } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import { ErrorInterceptorSkipHeader } from '../common/functions-and-constant/interceptor';
import { SUCCESSES } from '../common/utils/notification-constants';
import { ISnippetsItem } from '../models/space-recording';
import { URLService } from '../services/dynamic-url.service';
import { DownloadCapabilities } from '../services/feature-management.service';
import { NotificationToasterService } from '../services/notification-toaster.service';
import { SessionAccess, SessionRecordingAccess } from '../models/analytics';
import { AnalyticsSessionSpacePermissionsInput } from '../state/space.repository';
import { IFilterAPI } from '../standalones/components/generic-filters-view/filters.interface';
import {
  AnalyticsSessionsDetailsFilters,
  APIAnalyticsSessionsDetailsResponse,
  IAnalyticsSessionDetails,
  SessionDetailsEntrySource,
} from './models/analytics-sessions.model';

export interface AnalyticsSessionDetailsState {
  session: IAnalyticsSessionDetails | null;
  isLoading: boolean;
  isPublic: boolean;
  error: HttpErrorResponse | Error | null;
}

export enum AnalyticsSessionDetailsTab {
  OVERVIEW = 0,
  RECORDING = 1,
  INSIGHTS = 2,
  SNIPPETS = 3,
}

export interface VisibleSessionDetailTabsInput {
  hasRecordings: boolean;
  isSessionLive: boolean;
  showInsightsTab: boolean;
  showSnippetsTab: boolean;
}

export function getVisibleSessionDetailTabs(
  input: VisibleSessionDetailTabsInput,
): AnalyticsSessionDetailsTab[] {
  const tabs = [AnalyticsSessionDetailsTab.OVERVIEW];

  if (input.hasRecordings && !input.isSessionLive) {
    tabs.push(AnalyticsSessionDetailsTab.RECORDING);

    if (input.showInsightsTab) {
      tabs.push(AnalyticsSessionDetailsTab.INSIGHTS);
    }

    if (input.showSnippetsTab) {
      tabs.push(AnalyticsSessionDetailsTab.SNIPPETS);
    }
  }

  return tabs;
}

export function toMatTabIndex(
  tab: AnalyticsSessionDetailsTab,
  visibleTabs: AnalyticsSessionDetailsTab[],
): number {
  const index = visibleTabs.indexOf(tab);
  return index >= 0 ? index : 0;
}

export function fromMatTabIndex(
  matIndex: number,
  visibleTabs: AnalyticsSessionDetailsTab[],
): AnalyticsSessionDetailsTab {
  return visibleTabs[matIndex] ?? AnalyticsSessionDetailsTab.OVERVIEW;
}

export enum SessionItemPreviewVariant {
  SPACE = 'space',
  EVENT = 'event',
}

export interface AnalyticsSessionPermissions {
  download: boolean;
  /** Same role rule as `download`, but gated on CLOUD_RECORDING instead of DOWNLOAD_CLOUD_RECORDINGS */
  downloadTranscript: boolean;
  delete: boolean;
  changeSessionAccess: boolean;
  changeRecordingAccess: boolean;
  publicAccessEnabled: boolean;
  adminAccessEnabled: boolean;
  editSummary: boolean;
  isAdmin: boolean;
}

export const DEFAULT_ANALYTICS_SESSION_PERMISSIONS: AnalyticsSessionPermissions = {
  download: false,
  downloadTranscript: false,
  delete: false,
  adminAccessEnabled: false,
  publicAccessEnabled: false,
  changeSessionAccess: false,
  changeRecordingAccess: false,
  editSummary: false,
  isAdmin: false,
};

@Injectable({ providedIn: 'root' })
export class AnalyticsSessionsDetailsService {
  private sessionDetailsStateSubject$ = new BehaviorSubject<AnalyticsSessionDetailsState>({
    session: null,
    isLoading: false,
    isPublic: false,
    error: null,
  });
  readonly sessionDetailsState$ = this.sessionDetailsStateSubject$.asObservable();

  private _sessionUpdated$ = new Subject<IAnalyticsSessionDetails>();
  readonly sessionUpdated$ = this._sessionUpdated$.asObservable();

  private _sessionPermissionsUpdated$ = new BehaviorSubject<AnalyticsSessionPermissions>(
    DEFAULT_ANALYTICS_SESSION_PERMISSIONS,
  );
  readonly sessionPermissionsUpdated$ = this._sessionPermissionsUpdated$.asObservable();

  private entrySourceSubject$ = new BehaviorSubject<SessionDetailsEntrySource>(
    SessionDetailsEntrySource.DIRECT_LINK,
  );
  readonly entrySource$ = this.entrySourceSubject$.asObservable();

  constructor(
    private http: HttpClient,
    private urlService: URLService,
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
  ) {}

  get entrySource(): SessionDetailsEntrySource {
    return this.entrySourceSubject$.value;
  }

  // Callers set this right before opening the details; it is reset when the details close
  // so a later direct link isn't treated as coming from the previous source.
  setEntrySource(entrySource: SessionDetailsEntrySource): void {
    this.entrySourceSubject$.next(entrySource);
  }

  resetEntrySource(): void {
    this.entrySourceSubject$.next(SessionDetailsEntrySource.DIRECT_LINK);
  }

  isSingleSessionViewRouteFromSegments(segments: UrlSegment[]): boolean {
    return segments && segments.length === 2 && segments[0].path === 'sessions';
  }

  isSingleSessionViewRoute(url: string): boolean {
    return /^\/sessions\/[^/]+(?:\?.*)?(?:#.*)?$/.test(url);
  }

  extractSessionIdFromSegments(segments: UrlSegment[]): string | null {
    if (!segments || segments.length !== 2) {
      return null;
    }
    return segments[0].path === 'sessions' ? segments[1].path : null;
  }

  extractSessionIdFromUrl(url: string): string | null {
    const urlMatch = url.match(/^\/sessions\/([^/?#]+)/);
    return urlMatch && urlMatch[1] ? urlMatch[1] : null;
  }

  loadSessionDetails(sessionId: string): Observable<AnalyticsSessionDetailsState> {
    this.sessionDetailsStateSubject$.next({
      session: null,
      isLoading: true,
      isPublic: false,
      error: null,
    });

    return this.getSessionDetailsById(sessionId).pipe(
      tap((session) => {
        const isPublic = session.access === SessionAccess.PUBLIC;
        this.sessionDetailsStateSubject$.next({
          session,
          isLoading: false,
          isPublic,
          error: null,
        });
      }),
      switchMap(() => this.sessionDetailsStateSubject$.asObservable().pipe(first())),
      catchError((error) => {
        this.sessionDetailsStateSubject$.next({
          session: null,
          isLoading: false,
          isPublic: false,
          error,
        });
        return this.sessionDetailsStateSubject$.asObservable().pipe(first());
      }),
    );
  }

  getCachedSessionDetails(): AnalyticsSessionDetailsState | null {
    const state = this.sessionDetailsStateSubject$.value;
    const hasCachedState = Boolean(state.session) || Boolean(state.error);
    return hasCachedState ? state : null;
  }

  removeCachedSessionDetails(): void {
    this.sessionDetailsStateSubject$.next({
      session: null,
      isLoading: false,
      isPublic: false,
      error: null,
    });
  }

  getSessionDetailsById(sessionId: string): Observable<IAnalyticsSessionDetails> {
    const headerUpdates = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }).set(
        ErrorInterceptorSkipHeader,
        '',
      ),
    };
    return this.http.get<IAnalyticsSessionDetails>(
      `${this.urlService.getDynamicUrl()}/analytics/sessions/${sessionId}/details`,
      headerUpdates,
    );
  }

  getSessionsDetails(
    pageNumber: number,
    pageSize: number,
    dateRange?: { start?: Date | null; end?: Date | null },
    apiFilters?: IFilterAPI,
  ): Observable<APIAnalyticsSessionsDetailsResponse> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    const spaceIds = apiFilters?.spaces;
    const attendeeIds = apiFilters?.attendees;

    const filterObj: AnalyticsSessionsDetailsFilters = {
      dateStart: dateRange?.start ?? undefined,
      dateEnd: dateRange?.end ?? undefined,
      spaceIds: spaceIds?.length ? spaceIds : undefined,
      attendees: attendeeIds?.length ? attendeeIds : undefined,
      hasCalls: !!apiFilters?.hasCalls,
      hasRecording: !!apiFilters?.hasRecording,
    };

    params = params.set('filters', btoa(JSON.stringify(filterObj)));

    return this.http.get<APIAnalyticsSessionsDetailsResponse>(
      `${this.urlService.getDynamicUrl()}/analytics/sessions/details`,
      { params },
    );
  }

  public determineSessionPermissions(
    analyticsSessionPermissionsInput: AnalyticsSessionSpacePermissionsInput | undefined,
    isUserAdmin: boolean,
    isUserHost: boolean,
    isSessionPublic: boolean,
    downloadCapabilities: DownloadCapabilities,
  ): AnalyticsSessionPermissions {
    if (!analyticsSessionPermissionsInput) {
      return DEFAULT_ANALYTICS_SESSION_PERMISSIONS;
    }

    if (!analyticsSessionPermissionsInput.institution?._id) {
      return {
        download: isUserHost,
        downloadTranscript: isUserHost,
        delete: isUserHost,
        adminAccessEnabled: false,
        publicAccessEnabled: true,
        changeSessionAccess: isUserHost,
        changeRecordingAccess: isUserHost && !isSessionPublic,
        editSummary: isUserHost,
        isAdmin: false,
      };
    }

    const institutionSettings = analyticsSessionPermissionsInput.institution?.settings;
    if (!institutionSettings) {
      return DEFAULT_ANALYTICS_SESSION_PERMISSIONS;
    }

    const crEnableDownloads = institutionSettings.crEnableDownloads ?? false;
    const crEnableHostDeletion = institutionSettings.crEnableHostDeletion ?? false;
    const crForceAdminOnlyAccess = institutionSettings.crForceAdminOnlyAccess ?? false;
    const enablePublicSessions = institutionSettings.crEnablePublicRecordings ?? false;
    const userHasHostPermissions = isUserAdmin || isUserHost;

    // Recording and transcript share this role rule and differ only in the gating capability.
    const userCanDownload = isUserAdmin || (isUserHost && crEnableDownloads);

    const sessionPermissions: AnalyticsSessionPermissions = {
      download: userCanDownload && downloadCapabilities.recording,
      downloadTranscript: userCanDownload && downloadCapabilities.transcript,
      delete: isUserAdmin || (isUserHost && crEnableHostDeletion),
      changeSessionAccess: userHasHostPermissions,
      publicAccessEnabled: enablePublicSessions && userHasHostPermissions,
      changeRecordingAccess: !crForceAdminOnlyAccess && userHasHostPermissions && !isSessionPublic,
      adminAccessEnabled: isUserAdmin,
      editSummary: userHasHostPermissions,
      isAdmin: isUserAdmin,
    };

    this._sessionPermissionsUpdated$.next(sessionPermissions);
    return sessionPermissions;
  }

  /** `capabilityEnabled` is DOWNLOAD_CLOUD_RECORDINGS, which gates the recording download. */
  getDownloadRecordingTooltipText(capabilityEnabled: boolean, downloadEnabled: boolean): string {
    if (!capabilityEnabled) {
      return this.translateService.instant(
        'Downloading recordings is not part of your current plan',
      );
    } else if (!downloadEnabled) {
      return this.translateService.instant('You don’t have access to download this recording');
    } else {
      return this.translateService.instant(
        'Download will be available after recording has been processed',
      );
    }
  }

  /** `capabilityEnabled` is CLOUD_RECORDING, which gates the transcript download. */
  getDownloadTranscriptTooltipText(capabilityEnabled: boolean, downloadEnabled: boolean): string {
    if (!capabilityEnabled) {
      return this.translateService.instant(
        'Downloading transcripts is not part of your current plan',
      );
    } else if (!downloadEnabled) {
      return this.translateService.instant('You don’t have access to download this transcript');
    } else {
      return this.translateService.instant(
        'Download will be available after recording has been processed',
      );
    }
  }

  updateSessionAccess(
    sessionId: string,
    changes: { access?: SessionAccess; recordingAccess?: SessionRecordingAccess },
  ): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(
      `${this.urlService.getDynamicUrl()}/analytics/sessions/${sessionId}/access`,
      changes,
    );
  }

  approveAllSessionSnippets(sessionId: string): Observable<{ items: ISnippetsItem[] }> {
    return this.http.post<{ items: ISnippetsItem[] }>(
      `${this.urlService.getDynamicUrl()}/analytics/sessions/${sessionId}/snippets/approve-all`,
      {},
    );
  }

  updateSessionSnippet(
    sessionId: string,
    snippetId: string,
    payload: Pick<ISnippetsItem, 'title' | 'description'>,
  ): Observable<{ item: ISnippetsItem }> {
    return this.http.patch<{ item: ISnippetsItem }>(
      `${this.urlService.getDynamicUrl()}/analytics/sessions/${sessionId}/snippets/${snippetId}`,
      payload,
    );
  }

  deleteSessionSnippet(sessionId: string, snippetId: string): Observable<{ _id: string }> {
    return this.http.delete<{ _id: string }>(
      `${this.urlService.getDynamicUrl()}/analytics/sessions/${sessionId}/snippets/${snippetId}`,
    );
  }

  copySessionLink(sessionLink: string): void {
    navigator.clipboard.writeText(sessionLink);
    this.notificationToasterService.showDefaultSuccessNotification(
      this.translateService.instant('Link copied to clipboard'),
      SUCCESSES.COPY_LINK_TO_CLIPBOARD,
    );
  }

  updateSession(updatedSession: IAnalyticsSessionDetails): void {
    this._sessionUpdated$.next(updatedSession);
  }
}
